import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import {
  section2DetailsList,
  section5DetailsList,
  section8DetailsList,
  benefitDetailList,
  benefitSlideList,
  reviewList,
  bootCampFaqList,
  bootCampFaqTabList,
  curriculumDetailList,
  projectDetailList,
  projectOutputList,
  applyScheduleList,
} from '../../../../constants/kdt-ugm-5th'
import Link from 'next/link'
import { useMemo, useState } from 'react'
import Image from 'next/image'
import ReactPlayer from 'react-player'
import { useCopyUrlShare } from 'shared/hooks/useCopyUrlShare'
import styles from './kdt-ugm-5th.module.scss'
import { schoolNavList, ScrollNav } from './ScrollNav'
import { UgmAccordion } from './UgmAccordion'
import { UgmCurriculumM } from './UgmCurriculumM'
import UgmCurriculumPc from './UgmCurriculumPc'
import UgmProjectTab from './UgmProjectTab'
import {
  Section8SliderItem,
  BenefitSliderItem,
  ReviewSliderItem,
  ProjectOutputSliderItem,
  ProjectSliderItem,
} from './UgmSliderItem'
import LazyLoadSection from '@/components/LazyLoadSection'

interface IUnityKlass5thProps {
  applyStartedAt: string
  applyEndedAt: string
  startedAt: string
  endedAt: string
  totalSeconds: number
}

const UnityKlass5th = ({
  applyStartedAt,
  applyEndedAt,
  startedAt,
  endedAt,
  totalSeconds,
}: IUnityKlass5thProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [faqTab, setFaqTab] = useState('apply')
  const allFaqs = [
    ...bootCampFaqList.apply,
    ...bootCampFaqList.nebeka,
    ...bootCampFaqList.employment,
    ...bootCampFaqList.edu,
    ...bootCampFaqList.benefit,
    ...bootCampFaqList.etc,
  ]
  const faqList = faqTab === 'all' ? allFaqs : bootCampFaqList[faqTab]
  const onClickFaqTab = (value: string) => {
    setFaqTab(value)
  }
  const earlySessionDate = new Date('2025-04-30 21:00')
  const isAfterEarlySessionDate = useMemo(() => dayjs().isAfter(earlySessionDate), [])
  const { copyUrlShare } = useCopyUrlShare()

  // Section12_수강후기 하이라이트 텍스트
  const HighlightedText = ({ text, highlights }: { text: string; highlights: string[] }) => {
    const parts = text.split(new RegExp(`(${highlights.join('|')})`, 'g'))
    return (
      <p className="whitespace-pre-line text-xl font-bold leading-normal text-white">
        {parts.map(part =>
          highlights.includes(part) ? (
            <span key={`highlight-${part}`} className="text-yellow-450">
              {part}
            </span>
          ) : (
            <span key={`text-${part}`}>{part}</span>
          ),
        )}
      </p>
    )
  }

  dayjs.locale('ko')
  const convertAmPm = (time: string) => {
    return time.replace('AM', '오전').replace('PM', '오후')
  }

  const applyStart = convertAmPm(dayjs(applyStartedAt).format('YYYY.MM.DD.(dd) A h시'))
  const applyEnd = convertAmPm(dayjs(applyEndedAt).format('YYYY.MM.DD.(dd) A h시'))
  const applyBottomStart = convertAmPm(dayjs(applyStartedAt).format('MM월 DD일 (dd) A h시'))
  const applyBottomEnd = convertAmPm(dayjs(applyEndedAt).format('MM월 DD일 (dd) A h시'))
  const start = dayjs(startedAt).format('YYYY.MM.DD.(dd)')
  const end = dayjs(endedAt).format('YYYY.MM.DD.(dd)')
  const startBottom = dayjs(startedAt).format('YYYY년 MM월 DD일 (dd)')
  const endBottom = dayjs(endedAt).format('YYYY년 MM월 DD일(dd)')
  const totalHours = Math.ceil(totalSeconds / 3600)

  return (
    <div>
      {/* Section1_키비주얼 */}
      <section className="bg-[#FFC600]">
        <div className={`${styles.section1_kv_video} relative h-full w-full`}>
          <video
            src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section1_kv_video_ver1_${
              isMobile ? 'm' : 'pc'
            }.mp4`}
            muted
            autoPlay
            playsInline
            loop
          ></video>
        </div>
      </section>

      {/* Section2_핵심포인트 */}
      <LazyLoadSection height={684} mobileHeight={815} className="bg-gray-900 py-[34px] lg:py-20">
        <div className="mx-auto flex w-full max-w-xl flex-col gap-6 px-4 lg:max-w-7xl lg:gap-8 lg:px-6">
          <ul className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-stretch lg:justify-center lg:gap-6">
            {section2DetailsList.map(section2Details => (
              <li
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay={section2Details.delay}
                className="flex w-full flex-row items-center justify-between overflow-hidden rounded-lg bg-trueGray-800 px-4 py-3 lg:w-[calc(50%-12px)] lg:px-6 lg:py-5"
                key={section2Details.title}
              >
                <div className="flex flex-col gap-2">
                  <img
                    className="h-[23px] w-[53px] object-contain lg:h-[26px] lg:w-[68px]"
                    src={isMobile ? section2Details.stepUrlM : section2Details.stepUrlPc}
                    alt={section2Details.step}
                  />
                  <h6 className="whitespace-pre-line text-sm font-semibold leading-normal text-white lg:text-lg">
                    {section2Details.title}
                  </h6>
                </div>
                <img
                  className="h-16 w-16 object-contain lg:h-[88px] lg:w-[88px]"
                  src={section2Details.symbolUrl}
                  alt="-"
                />
              </li>
            ))}
          </ul>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="1100"
            className="flex w-full flex-col border-t border-label-neutral pt-6"
          >
            <li className="flex w-full flex-row items-start border-b border-label-neutral pb-3 lg:items-center lg:pb-4">
              <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 lg:w-[calc(33.33%+16px)] lg:gap-4">
                <img
                  className="h-5 w-5 object-contain lg:h-6 lg:w-6"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section2_icon1.png"
                  alt="-"
                />
                <p className="text-sm font-semibold text-white lg:text-base">모집기간</p>
              </h6>
              <p className="text-sm leading-normal text-white lg:w-2/3 lg:text-base">
                {applyStart} ~ <br className="block lg:hidden" />
                {applyEnd}
              </p>
            </li>
            <li className="flex w-full flex-row items-start pt-3 lg:items-center lg:pt-4">
              <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 lg:w-[calc(33.33%+16px)] lg:gap-4">
                <img
                  className="h-5 w-5 object-contain lg:h-6 lg:w-6"
                  src={
                    'https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section2_icon2.png'
                  }
                  alt="-"
                />
                <p className="text-sm font-semibold text-white lg:text-base">교육기간</p>
              </h6>
              <p className="text-sm leading-normal text-white lg:w-2/3 lg:text-base">
                {start} ~ {end} <br className="block lg:hidden" />
                평일 오전 9시 ~ <br className="block lg:hidden" />
                오후 6시 (일 8시간 교육)
              </p>
            </li>
          </ul>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="1300"
            className="flex w-full flex-col gap-3 lg:flex-row lg:gap-4"
          >
            <li className="flex w-full flex-row items-center justify-center rounded border border-yellow-450 bg-[rgba(255,119,16,0.1)] py-4 lg:rounded-lg lg:py-5">
              <h6 className="text-sm font-bold text-white lg:text-lg">
                내일배움카드로 교육비 무료
              </h6>
            </li>
            <li className="flex w-full flex-row items-center justify-center rounded border border-yellow-450 bg-[rgba(255,119,16,0.1)] py-4 lg:rounded-lg lg:py-5">
              <h6 className="text-sm font-bold text-white lg:text-lg">
                매월 최대 81만원 지원금 신청 가능
              </h6>
            </li>
          </ul>
        </div>
      </LazyLoadSection>

      <ScrollNav schoolNavList={schoolNavList} />

      {/* Section3_학습 난이도 */}
      <LazyLoadSection
        height={1062}
        mobileHeight={1289}
        className={`${styles.section3_bg} bg-gray-900 bg-cover bg-bottom bg-no-repeat pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36`}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-12 flex flex-1 flex-col items-center justify-center gap-2 lg:mb-16 lg:items-start lg:justify-start"
          >
            <p className="text-sm font-bold text-yellow-450 lg:text-lg">학습 난이도</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              잠깐! Unity 학습 난이도가 <br className="block lg:hidden" />
              궁금하다면?
            </h4>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="flex flex-row items-center justify-center rounded bg-[#1F1F1F] px-4 py-[18px] lg:justify-start lg:gap-[60px] lg:rounded-lg lg:px-12 lg:py-10"
          >
            <div className="relative hidden aspect-[887/500] max-w-[511px] shrink-0 lg:inline-block">
              <img
                className="relative z-10 inline-block w-full object-cover"
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section3_gifdeco_pc.png"
                alt="-"
              />
              <img
                className="absolute left-0 top-0 w-full object-cover"
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section3_why_unity_pc.gif"
                alt="-"
              />
            </div>
            <div className="flex w-full flex-col gap-2.5 lg:gap-3">
              <div className="flex flex-col gap-2.5 lg:gap-3">
                <p className="max-w-fit rounded-sm border border-yellow-450 px-2 py-1 text-xs font-bold text-yellow-450 lg:rounded lg:bg-[rgba(255,119,16,0.10)] lg:px-3 lg:font-semibold">
                  멋쟁이사자처럼 x Unity
                </p>
                <h6 className="text-base font-bold leading-normal text-white lg:text-xl lg:leading-snug">
                  Unity는 누구나 쉽고 빠르게 여러분의 꿈을
                  <br />
                  현실로 바꿔드려요!
                </h6>
              </div>
              <div className="relative aspect-[37/25] lg:hidden">
                <img
                  className="relative z-10 inline-block w-full object-cover"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section3_gifdeco_m.png"
                  alt="-"
                />
                <img
                  className="absolute left-0 top-0 inline-block w-full object-cover"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section3_why_unity_m.gif"
                  alt="-"
                />
              </div>
              <p className="text-xs text-label-neutral lg:hidden">
                *이해를 돕기 위한 참고용 Unity 기반 게임 이미지입니다.
              </p>
              <ul className="flex flex-col gap-2.5 lg:max-w-[570px] lg:gap-3">
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-label-assistive lg:text-base`}
                >
                  Windows · macOS · 콘솔 · 모바일 등 다양한 플랫폼 별 개발 작업을 최소화할 수 있어요
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-label-assistive lg:text-base`}
                >
                  C#이라는 유연한 프로그래밍 언어를 사용해서 개발 속도가 빨라요
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section3} text-sm leading-normal text-label-assistive lg:text-base`}
                >
                  아이디어 발굴부터 기획 · 마케팅 · 출시까지 게임 제작 전 과정을 직접 경험할 수
                  있어요
                </li>
              </ul>
            </div>
          </div>

          <ul className="mt-3 flex flex-col gap-3 lg:mt-6 lg:flex-row lg:gap-6">
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="500"
              className="flex w-full flex-col gap-4 rounded bg-[#1F1F1F] p-4 lg:w-auto lg:flex-1 lg:gap-5 lg:rounded-lg lg:px-6 lg:py-5"
            >
              <div className="flex w-full flex-row items-center justify-start gap-3 lg:flex-col lg:items-start lg:gap-5">
                <p className="flex h-5 w-5 items-center justify-center rounded-sm border border-yellow-450 bg-[rgba(255,119,16,0.1)] text-xs font-bold text-yellow-450 lg:h-6 lg:w-6 lg:text-lg">
                  1
                </p>
                <h6 className="text-base font-bold leading-normal text-white lg:text-lg">
                  Unity 혹은 게임 개발을 처음 접한다면
                </h6>
              </div>
              <p className="text-sm leading-normal text-label-assistive">
                기초반을 별도로 운영하여 유니티 · C# 기초부터 미니게임 제작 프로젝트까지, 유니티의
                기본까지 차근차근 배워서 본 과정을 따라갈 수 있도록 지원해요
              </p>
            </li>
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="700"
              className="flex w-full flex-col gap-4 rounded bg-[#1F1F1F] p-4 lg:w-auto lg:flex-1 lg:gap-5 lg:rounded-lg lg:px-6 lg:py-5"
            >
              <div className="flex w-full flex-row items-center justify-start gap-3 lg:flex-col lg:items-start lg:gap-5">
                <p className="flex h-5 w-5 items-center justify-center rounded-sm border border-yellow-450 bg-[rgba(255,119,16,0.1)] text-xs font-bold text-yellow-450 lg:h-6 lg:w-6 lg:text-lg">
                  2
                </p>
                <h6 className="text-base font-bold leading-normal text-white lg:text-lg">
                  전공 혹은 교육 기관을 통해 배운 경험이 있다면
                </h6>
              </div>
              <p className="text-sm leading-normal text-label-assistive">
                실무에서 주로 사용하는 스킬들을 디벨롭하고, 게임 출시를 위한 과정을 진행하여 새로운
                도전을 할 수 있어요
              </p>
            </li>
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="900"
              className="flex w-full flex-col gap-4 rounded bg-[#1F1F1F] p-4 lg:w-auto lg:flex-1 lg:gap-5 lg:rounded-lg lg:px-6 lg:py-5"
            >
              <div className="flex w-full flex-row items-center justify-start gap-3 lg:flex-col lg:items-start lg:gap-5">
                <p className="flex h-5 w-5 items-center justify-center rounded-sm border border-yellow-450 bg-[rgba(255,119,16,0.1)] text-xs font-bold text-yellow-450 lg:h-6 lg:w-6 lg:text-lg">
                  3
                </p>
                <h6 className="text-base font-bold leading-normal text-white lg:text-lg">
                  독학을 통해 개발한 경험이 있다면
                </h6>
              </div>
              <p className="text-sm leading-normal text-label-assistive">
                팀 프로젝트 활동을 통해 온라인 · 3D 핵앤슬래시 · 자율 주제 게임 제작 등 독학의
                한계를 동료들과 돌파할 수 있어요
              </p>
            </li>
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section4_프로젝트 */}
      <LazyLoadSection
        id="project"
        height={1757}
        mobileHeight={1175}
        className={`${styles.section4_bg} bg-gray-900 pt-[88px] lg:pt-36`}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-12 flex flex-col items-center justify-center lg:mb-16 lg:items-start lg:justify-start"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">
              단계별 프로젝트 : 메인 퀘스트
            </p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-left lg:text-4xl lg:leading-[1.4]">
              누구나 게임 출시까지 <br className="block lg:hidden" />
              경험할 수 있도록
              <br className="hidden lg:block" /> 준비한 <br className="block lg:hidden" />
              단계별 프로젝트 구성
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              게임 개발자의 가장 강력한 포트폴리오 &quot;게임 출시 경험&quot;
              <br />
              게임 유형별 제작 프로젝트부터 결제 시스템까지 구현하고{' '}
              <br className="block lg:hidden" />
              Skill-Up!
            </p>
            <h6 className="mt-12 text-center text-base font-bold text-label-alternative lg:mt-16 lg:text-left lg:text-2xl">
              프로젝트 구성
            </h6>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.project_accordion_wrap} hidden flex-row overflow-hidden rounded-lg bg-trueGray-800 lg:flex`}
          >
            <UgmProjectTab projectDetailList={projectDetailList} />
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.section4_slider} overflow-hidden lg:mb-16 lg:hidden lg:rounded-lg lg:bg-trueGray-800 lg:px-6 lg:py-10`}
          >
            <div className="lg:px-16">
              <ProjectSliderItem projectDetailList={projectDetailList} />
            </div>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="relative mt-[88px] flex flex-col gap-2 lg:mt-[251px] lg:gap-4"
          >
            <p className="text-center text-xl font-bold text-white lg:text-3xl">
              ”프로젝트만 하고 끝이 아니죠!”
            </p>
            <p className="text-center text-sm text-label-assistive lg:text-lg lg:text-white">
              나만의 게임을 완성했다면, 이제 세상에 내놓을 차례예요
            </p>
            <div className="absolute -bottom-[74.5px] left-1/2 -translate-x-1/2 lg:-bottom-[102px]">
              <div className={`${styles.section4_icon_arrow}`}>
                <img
                  className="h-[50px] w-[50px] object-contain lg:h-20 lg:w-20"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section4_icon_arrow.png"
                  alt="-"
                />
              </div>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section5_출시반 */}
      <LazyLoadSection
        height={748}
        mobileHeight={725}
        className="bg-[#313131] pb-[88px] pt-6 lg:pb-40 lg:pt-[60px]"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-12 flex flex-1 flex-col items-center justify-center gap-3 lg:mb-16 lg:items-start lg:justify-start lg:gap-5"
          >
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-left lg:text-4xl lg:leading-[1.4]">
              출시를 위한 심화 트랙
              <br />
              게임을 ‘만들었다’에서 ‘출시했다’로!
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              교육 중반 이후, 출시를 목표로 하는 모든 수강생이 모여
              <br />
              실무 중심의 특강 · 멘토링 · 피칭 등 <br className="block lg:hidden" />
              필요한 모든 것을 알려드려요
            </p>
          </div>

          <ul className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-stretch lg:justify-center lg:gap-6">
            {section5DetailsList.map(section5Details => (
              <li
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay={section5Details.delay}
                className="flex w-full flex-row items-center justify-between gap-3 overflow-hidden rounded-lg bg-trueGray-800 px-4 py-3 lg:w-[calc(50%-12px)] lg:gap-6 lg:px-6 lg:py-5"
                key={section5Details.title}
              >
                <img
                  className="h-[73px] w-[73px] object-contain lg:h-[88px] lg:w-[88px]"
                  src={section5Details.symbolUrl}
                  alt="-"
                />
                <div className="flex flex-1 flex-col gap-2">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.2)] px-3 py-1 text-[10px] font-bold text-yellow-450 lg:text-sm">
                    {section5Details.step}
                  </p>
                  <h6 className="text-sm font-semibold leading-normal text-white lg:whitespace-pre-line lg:text-lg">
                    {section5Details.title}
                  </h6>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section6_수료생 프로젝트 결과물 */}
      <LazyLoadSection
        height={1435}
        mobileHeight={654}
        className={`${styles.section6_bg} bg-trueGray-800 py-20 lg:pb-[216px] lg:pt-36`}
      >
        <div className="mx-auto w-full">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-12 flex flex-col items-center justify-center lg:mb-16"
          >
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
              게임 제작부터 출시까지 경험한
              <br />
              선배들의 포트폴리오
            </h4>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.section6_slider}`}
          >
            <ProjectOutputSliderItem contents={projectOutputList} />
          </div>
          <p className="mt-12 text-center text-xs text-label-alternative lg:mt-8 lg:text-base">
            *실제 유니티 게임 개발 부트캠프 수강생의 포트폴리오 입니다.
          </p>
        </div>
      </LazyLoadSection>

      {/* Section7_커리큘럼 */}
      <LazyLoadSection
        id="curriculum"
        height={2047}
        mobileHeight={1844}
        className={`${styles.section7_bg} bg-gray-900 bg-contain bg-top bg-no-repeat pt-[120px] lg:bg-cover lg:pt-[143px]`}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16 lg:items-start lg:justify-start"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">
              커리큘럼 안내 : 초고속 레이드
            </p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-left lg:text-4xl lg:leading-[1.4]">
              게임 제작에 필요한 스킬부터
              <br />
              출시를 위한 별도 교육까지 <br className="block lg:hidden" />
              단독 진행!
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              어떤 튜토리얼보다 쉽고 어떤 스킬트리보다 <br className="block lg:hidden" />
              강력한 커리큘럼으로 <br className="hidden lg:block" />
              게임 개발자를 향한 <br className="block lg:hidden" />
              초고속 레이드에 참여하세요!
            </p>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="hidden flex-col overflow-hidden lg:flex"
          >
            <UgmCurriculumPc curriculumDetailList={curriculumDetailList} />
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="flex flex-col overflow-hidden lg:hidden"
          >
            <UgmCurriculumM curriculumDetailList={curriculumDetailList} />
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="relative mt-[88px] flex flex-col gap-2 lg:mt-[251px] lg:gap-4"
          >
            <p className="text-center text-xl font-bold text-white lg:text-3xl">
              “C# · 유니티 프로그래밍이 <br className="block lg:hidden" />
              처음이신가요?”
            </p>
            <div className="absolute -bottom-[74.5px] left-1/2 -translate-x-1/2 lg:-bottom-[102px]">
              <div className={`${styles.section4_icon_arrow}`}>
                <img
                  className="h-[50px] w-[50px] object-contain lg:h-20 lg:w-20"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section4_icon_arrow.png"
                  alt="-"
                />
              </div>
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section8_비전공자 안내 */}
      <LazyLoadSection
        height={907}
        mobileHeight={790}
        className="bg-[#EEEEEE] pb-[88px] pt-10 lg:pb-[180px] lg:pt-[60px]"
      >
        <div className="mx-auto w-full max-w-xl lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-12 flex flex-col items-center justify-center gap-3 lg:mb-16 lg:gap-5"
          >
            <h4 className="text-center text-2xl font-bold leading-snug text-label-normal lg:text-4xl lg:leading-[1.4]">
              걱정하지 마세요! 비전공자도 <br className="block lg:hidden" />
              끝까지 <br className="hidden lg:block" />
              완주할 수 있도록 준비한 <br className="block lg:hidden" />{' '}
              <span className="text-yellow-450">맞춤형 기초 프로그램</span>
            </h4>
            <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
              처음이기에 더더욱 제대로 배울 수 있도록 함께 할게요!
            </p>
          </div>
          <ul className="hidden w-full flex-row items-stretch justify-center gap-6 lg:flex">
            {section8DetailsList.map(section8Details => (
              <li
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay={section8Details.delay}
                className="flex h-full flex-1 flex-col items-center justify-center overflow-hidden rounded-lg"
                key={section8Details.title}
              >
                <div className="h-auto w-full object-contain">
                  <Image
                    src={section8Details.imgUrlPc}
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="-"
                  />
                </div>
                <div className="flex min-h-[254px] flex-1 flex-col rounded-b-lg border border-[#D4D4D4] bg-white p-6">
                  <p className="mb-2 max-w-fit rounded bg-[rgba(255,119,16,0.1)] px-2 py-1 text-sm font-bold text-yellow-450">
                    {section8Details.step}
                  </p>
                  <h6 className="mb-4 whitespace-pre-line text-lg font-bold leading-normal text-label-normal">
                    {section8Details.title}
                  </h6>
                  <p className="flex-1 text-base leading-normal text-label-normal">
                    {section8Details.desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.section8_slider} lg:hidden`}
          >
            <Section8SliderItem contents={section8DetailsList} />
          </div>
        </div>
      </LazyLoadSection>

      {/* Section9_강사진 */}
      <LazyLoadSection
        id="teacher"
        height={1106}
        mobileHeight={1395}
        className={`${styles.section9_bg} bg-gray-900 bg-cover bg-bottom bg-no-repeat pb-[120px] pt-[88px] lg:pb-[184px] lg:pt-36`}
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-10 flex flex-col items-center justify-center lg:mb-16 lg:items-start lg:justify-start"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">
              강사진 : 전직 NPC 등장!
            </p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-left lg:text-4xl lg:leading-[1.4]">
              역대급 스펙의 강사진에게 배우는
              <br />
              Unity 게임 개발의 모든 것
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              게임 개발자로서의 모든 스킬을 갖춘 강사진!
              <br />
              업계 최전선에서 활약하는 강사님의 지식을 <br className="block lg:hidden" />
              아낌없이 전수합니다.
            </p>
          </div>
          <ul className="mx-auto flex flex-col gap-3 lg:flex-row lg:gap-6">
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex flex-1 flex-col items-start rounded-lg bg-trueGray-800 p-4 lg:p-6"
            >
              <div className="flex flex-row gap-4 lg:gap-6">
                <div className="h-[70px] w-[70px] object-contain lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section9_img1.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="정대희 강사님 심볼 이미지"
                  />
                </div>
                <div className="flex flex-col items-start justify-center lg:gap-0.5">
                  <p className="text-xs font-bold text-yellow-450 lg:text-sm">주강사 1</p>
                  <h6 className="text-base font-bold text-white lg:text-lg">정대희 강사님</h6>
                </div>
              </div>
              <hr className="my-4 w-full border border-[#404040] lg:my-6" />
              <dl className="mb-4 flex flex-col gap-1 rounded bg-[#4A4A4A] p-4 lg:mb-6 lg:gap-2">
                <dt className="text-sm font-bold text-white lg:text-base">강사님 한마디</dt>
                <dd className="text-sm text-white lg:text-base">
                  “실전에서 바로 통하는 Unity 개발 역량을 누구보다 깊이 있게 안내합니다”
                </dd>
              </dl>
              <div className="flex flex-col gap-1 lg:gap-0.5">
                <p
                  className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-white lg:text-base`}
                >
                  Unity 공식 인증 강사
                </p>
                <p
                  className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-white lg:text-base`}
                >
                  KAIST 대학원 · 라이크 코퍼레이션 등 유수의 교육기관에서 커리큘럼 설계 및 강의 진행
                </p>

                <p
                  className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-white lg:text-base`}
                >
                  SK On 배터리 공정 디지털 트윈 · LG전자 AR 기반 사내 교육 시스템 개발 등 산업계
                  실전 프로젝트 다수 주도
                </p>
              </div>
            </li>
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="500"
              className="flex flex-1 flex-col items-start rounded-lg bg-trueGray-800 p-4 lg:p-6"
            >
              <div className="flex flex-row gap-4 lg:gap-6">
                <div className="h-[70px] w-[70px] object-contain lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section9_img2.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="고준일 강사님 심볼 이미지"
                  />
                </div>
                <div className="flex flex-col items-start justify-center lg:gap-0.5">
                  <p className="text-xs font-bold text-yellow-450 lg:text-sm">주강사 2</p>
                  <h6 className="text-base font-bold text-white lg:text-lg">고준일 강사님</h6>
                </div>
              </div>
              <hr className="my-4 w-full border border-[#404040] lg:my-6" />
              <dl className="mb-4 flex flex-col gap-1 rounded bg-[#4A4A4A] p-4 lg:mb-6 lg:gap-2">
                <dt className="text-sm font-bold text-white lg:text-base">강사님 한마디</dt>
                <dd className="text-sm text-white lg:text-base">
                  “출시를 위한 높은 퀄리티의 강의와 프로젝트 리딩을 약속합니다”
                </dd>
              </dl>
              <div className="flex flex-col gap-1 lg:gap-0.5">
                <p
                  className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-white lg:text-base`}
                >
                  Unity 공식 인증 강사
                </p>
                <p
                  className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-white lg:text-base`}
                >
                  화제의 게임 ‘서울역’ 제작자
                </p>
                <p
                  className={`${styles.text_dot} ${styles.text_dot_section9} text-sm leading-normal text-white lg:text-base`}
                >
                  명지전문대학교 · 호서대학교 게임학과 겸임교수
                </p>
              </div>
            </li>
            <li
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="700"
              className="flex flex-1 flex-col items-start rounded-lg bg-trueGray-800 p-4 lg:px-6 lg:py-5"
            >
              <div className="flex flex-row gap-4 lg:gap-6">
                <div className="h-[70px] w-[70px] object-contain lg:h-[120px] lg:w-[120px]">
                  <Image
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section9_img3.png"
                    layout="fill"
                    objectFit="cover"
                    className="!relative w-full"
                    alt="특강 강사 심볼 이미지"
                  />
                </div>
                <div className="flex flex-col items-start justify-center lg:gap-0.5">
                  <p className="text-xs font-bold text-yellow-450 lg:text-sm">특강 강사 정보</p>
                  <h6 className="text-base font-bold text-white lg:text-lg">강사진 이력</h6>
                </div>
              </div>
              <hr className="my-4 w-full border border-[#404040] lg:my-6" />
              <div className="flex flex-col gap-1">
                <p className="text-sm leading-normal lg:text-base">
                  <span className="font-bold text-yellow-450">전상훈 특강 강사님</span>{' '}
                  <span className="text-white">: 라이엇 게임즈 기획자</span>
                </p>
                <p className="text-sm leading-normal lg:text-base">
                  <span className="font-bold text-yellow-450">추가 특강 강사님</span>{' '}
                  <span className="text-white">: 전) 넥슨, 네오플 기획자</span>
                </p>
                <p className="text-sm leading-normal lg:text-base">
                  <span className="font-bold text-yellow-450">이유원 특강 강사님</span>{' '}
                  <span className="text-white">: 반지하 게임즈 대표</span>
                </p>
                <p className="text-sm leading-normal lg:text-base">
                  <span className="font-bold text-yellow-450">정광민 특강 강사님</span>{' '}
                  <span className="text-white">: 세인넛츠 스튜디오 대표</span>
                </p>
              </div>
            </li>
          </ul>
        </div>
      </LazyLoadSection>

      {/* Section10_사전설명회 */}
      {isAfterEarlySessionDate ? (
        <LazyLoadSection
          height={1161}
          mobileHeight={475}
          className="bg-[#230F0E] pb-[104px] pt-[88px] lg:pb-40 lg:pt-36"
        >
          <div className="mx-auto flex w-full max-w-xl flex-col items-center justify-center px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-6 flex w-full flex-col lg:mb-16"
            >
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                강사님과 실시간 라이브로 진행된
                <br />
                사전설명회 다시보기
              </h4>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="relative w-full"
              style={{
                paddingTop: '56.25%',
              }}
            >
              <ReactPlayer
                alt="멋쟁이사자처럼 이세계 유니티 5기 사전설명회 LIVE"
                url="https://youtube.com/live/yoXouz_0D-Q?feature=share"
                controls={true}
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
          </div>
        </LazyLoadSection>
      ) : (
        <LazyLoadSection
          height={617}
          mobileHeight={649}
          className="bg-[#230F0E] pb-[104px] pt-[88px] lg:pb-40 lg:pt-36"
        >
          <div className="mx-auto flex w-full max-w-xl flex-col items-center justify-center lg:max-w-7xl lg:flex-row lg:justify-between lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className={`${styles.section10_deco} relative hidden aspect-[302/153] w-1/2 max-w-[604px] overflow-hidden lg:inline-block`}
            >
              <img
                className="relative z-10 hidden h-full w-full object-cover lg:block"
                src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section10_img_deco_${
                  isMobile ? 'm' : 'pc'
                }.png`}
                alt="-"
              />
              <img
                className="h-[calc(100%-2px)] w-[calc(100%-1px)] object-cover lg:absolute lg:left-0 lg:top-[1px]"
                src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section10_pre_live_${
                  isMobile ? 'm' : 'pc'
                }.gif`}
                alt="유니티 게임 개발 사전설명회 라이브 예시 이미지"
              />
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex w-full flex-col lg:w-auto lg:min-w-[455px]"
            >
              <h4 className="mb-3 text-center text-2xl font-bold text-white lg:mb-4 lg:text-left lg:text-4xl">
                사전설명회 안내
              </h4>
              <p className="mb-6 text-center text-sm leading-normal text-white lg:mb-4 lg:text-left lg:text-lg">
                유니티 게임 개발 5기에 궁금한 점이 있다면
                <br />
                실시간 온라인 사전설명회를 통해 <br className="block lg:hidden" />
                강사님께 직접 물어보고 <br className="hidden lg:block" />
                확인해 보세요!
              </p>
              <div className={`${styles.section10_deco} relative mx-4 mb-6 block lg:hidden`}>
                <img
                  className="relative z-10 w-full object-cover"
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section10_img_deco_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  alt="-"
                />
                <img
                  className="absolute left-0 top-[1px] w-full object-cover"
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section10_pre_live_${
                    isMobile ? 'm' : 'pc'
                  }.gif`}
                  alt="유니티 게임 개발 사전설명회 라이브 예시 이미지"
                />
              </div>
              <p className="relative z-[2] mb-6 text-center text-sm font-bold leading-normal text-yellow-450 lg:mb-[36px] lg:text-left lg:text-lg">
                일정 : 4월 30일 (수) 오후 7시
                <br />
                장소 : Youtube 라이브 스트리밍
              </p>
              <div className="relative z-10 mx-4 lg:mx-0 lg:max-w-[395px]">
                <Link
                  href="https://likelion2.typeform.com/to/EOrpaF4m"
                  target="_blank"
                  className={`relative z-10 flex w-full items-center justify-center bg-cover bg-center bg-no-repeat text-sm font-bold text-white lg:text-base`}
                  style={{
                    paddingTop: isMobile ? '14.79%' : '16.46%',
                  }}
                >
                  <div className="absolute left-1/2 top-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 flex-row items-center justify-center gap-2">
                    사전 설명회 신청하기
                    <img
                      className="h-4 w-4 lg:h-5 lg:w-5"
                      src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-3rd/section7_icon.png"
                      alt="-"
                    />
                  </div>
                </Link>
                <img
                  className="absolute left-0 top-0 w-full"
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-4th/section7_btn_bg_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  alt="-"
                />
              </div>
            </div>
          </div>
        </LazyLoadSection>
      )}

      {/* Section11_취업지원 */}
      <LazyLoadSection
        id="benefit"
        height={2301}
        mobileHeight={1323}
        className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-10 flex flex-1 flex-col items-center justify-center lg:mb-[120px] lg:items-start lg:justify-start"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">
              취업 지원 : 초고속 성장 패키지
            </p>
            <h4 className="mb-3 text-center text-2xl font-bold leading-snug text-white lg:mb-5 lg:text-left lg:text-4xl lg:leading-[1.4]">
              단 한 명의 낙오자도 허용하지 않는
              <br />
              초고속 성장 패키지
            </h4>
            <p className="text-center text-sm leading-normal text-label-assistive lg:text-left lg:text-lg">
              더 이상 초고속 성장을 위한 유료 결제는 그만! <br className="block lg:hidden" />
              현직 개발자의 특강부터 모의 면접까지 <br className="hidden lg:hidden" />
              여러분의 성장과 <br className="block lg:hidden" />
              취업에 필요한 모든 혜택을 무료로 즐기세요!
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.curriculum_accordion_wrap} mb-10 flex flex-col overflow-hidden lg:mb-[120px]`}
          >
            <h6 className="mb-6 text-center text-base font-semibold text-label-alternative lg:mb-12 lg:text-left lg:text-[28px] lg:font-bold">
              초고속 취업 지원 패키지
            </h6>
            <UgmAccordion benefitDetailList={benefitDetailList} />
            <p className="mt-3 text-center text-xs leading-normal text-label-alternative lg:mt-12 lg:text-left lg:text-base">
              * 교육생 훈련 상황 및 회사 사정에 따라 지원 내용이 <br className="block lg:hidden" />
              변동 될 수 있습니다.
            </p>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="flex flex-col overflow-hidden"
          >
            <h6 className="mb-6 text-center text-base font-semibold text-label-alternative lg:mb-12 lg:text-left lg:text-[28px] lg:font-bold">
              초고속 성장 지원 패키지
            </h6>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="hidden flex-row flex-wrap gap-[25.5px] lg:flex"
            >
              {benefitSlideList.map(({ desc, title, imgUrl }) => (
                <div
                  className="flex h-[270px] w-[calc(33.33%-17px)] flex-col justify-between gap-[21.26px] rounded-lg bg-gray-900 px-[25.5px] py-[21.26px]"
                  key={title}
                >
                  <div className="flex flex-col gap-1">
                    <h6 className="mb-1 text-[19.1px] font-bold text-white">{title}</h6>
                    <p className="whitespace-pre-wrap text-[17px] leading-normal text-label-assistive">
                      {desc}
                    </p>
                  </div>
                  <img
                    src={imgUrl}
                    className="block h-[95.66px] w-full object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="block lg:hidden"
            >
              <BenefitSliderItem contents={benefitSlideList} />
            </div>
          </div>
        </div>
      </LazyLoadSection>

      {/* Section12_수강후기 */}
      <LazyLoadSection
        height={1351}
        mobileHeight={735}
        className="bg-gray-900 pb-[104px] pt-[88px] lg:pb-40 lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl lg:max-w-7xl lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-4 flex flex-col items-center justify-center lg:mb-6"
          >
            <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">수강 후기</p>
            <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
              선배들의 생생한 이야기
            </h4>
          </div>

          <div className="mx-auto mb-12 flex max-w-[253px] flex-row items-center justify-center gap-1 lg:mb-16 lg:max-w-[410px] lg:gap-[6.5px]">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="h-auto w-12 lg:w-[78px]"
            >
              <Image
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_box_deco_left.png"
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt="-"
              />
            </div>
            <div className="flex flex-1 flex-col items-center justify-center gap-1 lg:gap-2">
              <p
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="100"
                className="text-sm font-bold text-white"
              >
                이전 기수 수료생 만족도
              </p>
              <div className="flex flex-row items-center justify-center gap-1 lg:gap-[6.5px]">
                <img
                  data-aos-once="true"
                  data-aos="fade-up"
                  data-aos-easing="ease"
                  data-aos-duration="400"
                  data-aos-delay="300"
                  className="h-auto w-[26px] object-contain lg:w-[43px]"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_star_full.png"
                  alt="full star"
                />
                <img
                  data-aos-once="true"
                  data-aos="fade-up"
                  data-aos-easing="ease"
                  data-aos-duration="400"
                  data-aos-delay="500"
                  className="h-auto w-[26px] object-contain lg:w-[43px]"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_star_full.png"
                  alt="full star"
                />
                <img
                  data-aos-once="true"
                  data-aos="fade-up"
                  data-aos-easing="ease"
                  data-aos-duration="400"
                  data-aos-delay="700"
                  className="h-auto w-[26px] object-contain lg:w-[43px]"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_star_full.png"
                  alt="full star"
                />
                <img
                  data-aos-once="true"
                  data-aos="fade-up"
                  data-aos-easing="ease"
                  data-aos-duration="400"
                  data-aos-delay="900"
                  className="h-auto w-[26px] object-contain lg:w-[43px]"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_star_full.png"
                  alt="full star"
                />
                <img
                  data-aos-once="true"
                  data-aos="fade-up"
                  data-aos-easing="ease"
                  data-aos-duration="400"
                  data-aos-delay="1100"
                  className="h-auto w-[26px] object-contain lg:w-[43px]"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_star_half.png"
                  alt="half star"
                />
              </div>
              <p
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="100"
                className="text-base font-bold text-white lg:text-2xl"
              >
                4.5점
              </p>
              <p
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="100"
                className="text-xs text-label-alternative lg:text-xl"
              >
                2025.03.25 기준
              </p>
            </div>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="h-auto w-12 lg:w-[78px]"
            >
              <Image
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section12_box_deco_right.png"
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt="-"
              />
            </div>
          </div>

          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="mx-auto hidden flex-col gap-6 lg:flex"
          >
            {reviewList.map(review => (
              <li
                className="flex w-full flex-row items-center gap-6 rounded-lg bg-trueGray-800 px-6 py-5"
                key={review.imgUrl}
              >
                <div className="h-[84px] shrink-0 border-r border-[#525252] pr-6">
                  <div className="h-[84px] w-[84px]">
                    <Image
                      src={review.imgUrl}
                      layout="fill"
                      objectFit="cover"
                      className="!relative w-full"
                      alt="-"
                    />
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <HighlightedText text={review.review} highlights={review.highlights} />
                </div>
              </li>
            ))}
          </ul>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.section12_slider} lg:hidden`}
          >
            <ReviewSliderItem contents={reviewList} />
          </div>
        </div>
      </LazyLoadSection>

      {/* section13_교육일정 */}
      <LazyLoadSection
        id="condition"
        height={548}
        mobileHeight={699}
        className="bg-black pb-20 pt-[72px] lg:pb-36 lg:pt-[120px]"
      >
        <div className="mx-auto flex w-full max-w-xl flex-col gap-20 px-4 lg:mx-auto lg:max-w-7xl lg:flex-row lg:gap-6 lg:px-6">
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="w-full lg:w-auto lg:flex-1"
          >
            <div className="mb-6 lg:mb-8">
              <h4 className="text-center text-2xl font-bold text-white lg:text-4xl">
                지원 및 선발 안내
              </h4>
            </div>

            <div className="flex flex-col gap-3 lg:min-h-[212px] lg:gap-0 lg:rounded-lg lg:bg-trueGray-800 lg:px-6 lg:py-5">
              <ul className="rounded-lg bg-trueGray-800 px-4 lg:rounded-b-none lg:px-0">
                <li className="flex flex-col gap-2 py-3 lg:mb-3 lg:flex-row lg:items-center lg:justify-center lg:gap-5 lg:border-b-[0.5px] lg:border-[#3D3D3D] lg:py-0 lg:pb-3">
                  <b className="w-full text-sm font-semibold leading-normal text-yellow-450 lg:w-[95px] lg:text-base lg:font-bold">
                    모집 기간
                  </b>
                  <p className="flex-1 whitespace-pre-line text-sm text-label-assistive lg:whitespace-normal lg:text-base">
                    {applyBottomStart} ~ {applyBottomEnd}
                  </p>
                </li>
              </ul>

              {applyScheduleList.map(apply => (
                <ul className="rounded-lg bg-trueGray-800 px-4 lg:rounded-b-none lg:px-0">
                  <li
                    className="flex flex-col gap-2 py-3 lg:flex-row lg:items-center lg:justify-center lg:gap-5 lg:py-0"
                    key={apply.title}
                  >
                    <b className="w-full text-sm font-semibold leading-normal text-yellow-450 lg:w-[95px] lg:text-base lg:font-bold">
                      {apply.title}
                    </b>
                    <p className="flex-1 whitespace-pre-line text-sm text-label-assistive lg:whitespace-normal lg:text-base">
                      {apply.content}
                    </p>
                  </li>
                </ul>
              ))}
            </div>
          </div>
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="w-full lg:w-auto lg:flex-1"
          >
            <div className="mb-6 lg:mb-8">
              <h4 className="text-center text-2xl font-bold text-white lg:text-4xl">
                교육 일정 안내
              </h4>
            </div>
            <ul className="rounded-lg bg-trueGray-800 px-4 py-3 lg:h-[212px] lg:px-6 lg:py-5">
              <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#3D3D3D] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-3">
                <b className="w-[53px] text-sm font-semibold leading-normal text-yellow-450 lg:w-[72px] lg:text-base lg:font-bold">
                  OT&개강
                </b>
                <p className="flex-1 whitespace-pre-line text-sm text-label-assistive lg:whitespace-normal lg:text-base">
                  2025년 05월 13일 (화) 오후 1시
                </p>
              </li>
              <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#3D3D3D] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-3">
                <b className="w-[53px] text-sm font-semibold leading-normal text-yellow-450 lg:w-[72px] lg:text-base lg:font-bold">
                  교육 기간
                </b>
                <p className="flex-1 whitespace-pre-line text-sm text-label-assistive lg:whitespace-normal lg:text-base">
                  {startBottom} ~ <br className="block lg:hidden" />
                  {endBottom}
                </p>
              </li>
              <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#3D3D3D] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-3">
                <b className="w-[53px] text-sm font-semibold leading-normal text-yellow-450 lg:w-[72px] lg:text-base lg:font-bold">
                  교육 시간
                </b>
                <p className="flex-1 whitespace-pre-line text-sm text-label-assistive lg:whitespace-normal lg:text-base">
                  {totalHours}시간 / 평일 9시 ~ 18시 <br className="block lg:hidden" />
                  (점심시간 12시 ~ 13시 제외)
                </p>
              </li>
              <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#3D3D3D] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-3">
                <b className="w-[53px] text-sm font-semibold leading-normal text-yellow-450 lg:w-[72px] lg:text-base lg:font-bold">
                  학습 형태
                </b>
                <p className="flex-1 whitespace-pre-line text-sm text-label-assistive lg:whitespace-normal lg:text-base">
                  실시간 온라인 교육
                </p>
              </li>
            </ul>
          </div>
        </div>
      </LazyLoadSection>

      {/* section14_자주묻는질문 */}
      <LazyLoadSection
        height={1308}
        mobileHeight={1008}
        className="bg-white pb-[120px] pt-20 lg:pb-[160px] lg:pt-36"
      >
        <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
          <h4
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mb-4 text-center text-2xl font-bold text-label-normal lg:mb-16 lg:text-4xl"
          >
            자주 묻는 질문
          </h4>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className="mx-auto mb-10 flex max-w-[324px] flex-wrap items-center justify-center gap-1.5 lg:mb-16 lg:max-w-none lg:flex-nowrap lg:gap-2"
          >
            {bootCampFaqTabList.map(tabItem => {
              const isActive = tabItem.value === faqTab
              return (
                <li
                  className={`${
                    isActive
                      ? 'bg-yellow-450 text-white'
                      : 'border border-normal bg-white text-label-alternative'
                  } cursor-pointer rounded-full px-5 py-1.5 text-xs font-bold lg:px-6 lg:py-2 lg:text-sm`}
                  onClick={() => onClickFaqTab(tabItem.value)}
                  key={tabItem.value}
                >
                  {tabItem.label}
                </li>
              )
            })}
          </ul>
          <ul
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className="mx-auto flex flex-col"
          >
            {faqList.map(faq => (
              <li
                className={`${styles.faqItem} border-b border-[#E5E5E5] py-4 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-6`}
                key={faq.question}
              >
                <details className="group overflow-hidden">
                  <summary className="cursor-pointer list-none group-open:pb-1 lg:group-open:pb-2">
                    <div className="flex w-full items-center justify-between gap-4">
                      <div>
                        <p
                          className={`${styles.faq_item_title} text-sm font-semibold text-label-normal lg:text-lg`}
                        >
                          {faq.question}
                        </p>
                      </div>
                      <ChevronDownIcon className="w-4 shrink-0 text-label-neutral group-open:rotate-180 lg:w-6" />
                    </div>
                  </summary>
                  <div className="pl-5 lg:pl-[22px]">
                    <div className="whitespace-pre-wrap text-sm leading-normal text-label-neutral lg:text-base">
                      {faq.answer && (
                        <span className="max-w-full">
                          {faq.answer}
                          {faq.externalUrl1 && (
                            <>
                              {' '}
                              자세한 발급 과정은 아래 링크를 통해 확인 가능합니다.
                              <br />-{' '}
                              <Link href={faq.externalUrl1} target="_blank" className="underline">
                                {faq.urlName1}
                              </Link>
                              {faq.secondAnswer}
                            </>
                          )}
                          {faq.externalUrl2 && (
                            <>
                              <br />-{' '}
                              <Link href={faq.externalUrl2} target="_blank" className="underline">
                                {faq.urlName2}
                              </Link>
                            </>
                          )}
                        </span>
                      )}
                      {faq.answerList &&
                        faq.answerList.map(el => (
                          <p key={el} className="text-sm text-label-neutral lg:text-base">
                            {el}
                          </p>
                        ))}
                    </div>
                  </div>
                </details>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col items-center justify-between gap-3 rounded-md bg-[#FFF3EB] p-3 lg:mt-16 lg:flex-row lg:items-center lg:rounded-lg lg:p-6 lg:pl-16">
            <p className="w-full text-left text-base font-semibold text-label-normal lg:w-auto lg:flex-1 lg:text-lg lg:font-bold">
              더 궁금한 점이 있다면 <br className="block lg:hidden" />
              언제든지 교육 매니저에게 물어보세요!
            </p>

            <Link
              href="https://likelion.channel.io/workflows/741651"
              target="_blank"
              className="flex h-[36px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-white lg:h-[64px] lg:w-[369px] lg:rounded-lg"
            >
              <span className="text-base font-bold text-yellow-450 lg:text-lg">1:1 문의하기</span>
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/icon_go_link.png"
                className="h-[18px] w-[18px] shrink-0 object-contain lg:h-4 lg:w-4"
                alt="1:1 문의하기"
              />
            </Link>
          </div>
          <div className="mt-4 flex flex-col items-center justify-between gap-3 rounded-md bg-strong p-3 lg:mt-5 lg:flex-row lg:items-center lg:rounded-lg lg:p-6 lg:pl-16">
            <p className="w-full text-left text-base font-semibold text-label-normal lg:w-auto lg:flex-1 lg:text-lg lg:font-bold">
              유니티 게임 개발자로 함께 성장하고 싶은 친구에게 <br className="block lg:hidden" />
              공유해 보세요!
            </p>
            <div
              className="flex h-[36px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-white lg:h-[64px] lg:w-[369px] lg:rounded-lg"
              onClick={() => copyUrlShare('https://bootcamp.likelion.net/school/kdt-ugm-5th')}
            >
              <span className="text-base font-bold text-yellow-450 lg:text-lg">링크 복사하기</span>
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/icon_copy_share.png"
                className="h-[18px] w-[18px] shrink-0 object-contain lg:h-4 lg:w-4"
              />
            </div>
          </div>
        </div>
      </LazyLoadSection>
    </div>
  )
}

export default UnityKlass5th
